import React, { lazy, Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
// react-helmet-async removed in the React 19 migration; metadata is managed
// imperatively by src/components/SEO/index.jsx now.
// ThemeProvider + StyledEngineProvider + createTheme from @mui/material/styles.
// This pulls the `mui-theme` chunk (small — just the createTheme machinery
// and palette/typography defaults). The heavy components (Dialog, Select,
// Stepper, …) live in the separate `mui-material` chunk and only load when
// a route actually mounts them. See manualChunks split in vite.config.js.
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
// Sentry is dynamically imported at idle time below so its ~111 KB chunk does
// not block FCP. Top-of-file `import * as Sentry` pulled the whole SDK into
// the entry chunk even though Sentry.init was scheduled with
// requestIdleCallback.
import config from './config.json';
import Routes from 'routes';
import ErrorBoundary from 'components/ErrorBoundary';
import ServiceMessage from 'components/ServiceMessage';
import i18n, { i18nPromise } from './i18n';
import theme from 'theme';
import store from 'store';
import { fetchFeatures } from 'actions/features';
import { installChunkRecovery } from './helpers/chunkRecovery';
import { dropResourceSpans, isCloudflareBeaconError } from './helpers/sentryNoise';

if (typeof window !== 'undefined') installChunkRecovery(window);
// flag-icons CSS deliberately NOT imported here — it's 86 KB unminified
// (~85 KB unused on the home route) and was the largest single render-blocking
// asset. The CSS now ships with the consumers that actually render flags:
// src/components/Footer.jsx and src/pages/Localizations/index.jsx.

// DSN comes from the brand-specific config.{profitroll,jobalto}.json swapped
// in at build time. Empty DSN → Sentry.init becomes a no-op (desired in dev).
// Sentry DSN: env var wins (baked in at Docker build via REACT_APP_SENTRY_DSN),
// config.sentryDsn from the brand JSON is a dev-mode fallback.
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || config.sentryDsn;
if (SENTRY_DSN && typeof window !== 'undefined') {
  // Defer Sentry.init until idle. browserTracingIntegration installs perf
  // observers and patches fetch/XHR — non-trivial work that previously ran
  // synchronously at module top-of-file, before React even mounted, costing
  // hundreds of ms on the critical path. requestIdleCallback yields after
  // first paint; the 5 s timeout is the safety floor so error capture is
  // active before the user is likely to trigger one.
  // Errors thrown during this window are missed — acceptable trade since
  // the previous setup also dropped errors during cold-start before init
  // returned.
  const initSentry = async () => {
    // Dynamic import so the entire @sentry/react bundle ships as its own
    // chunk loaded off the critical path. The if (SENTRY_DSN) gate above
    // also means we skip the import entirely in dev when DSN is empty.
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: config.application?.environment || 'development',
      sendDefaultPii: !!config.sentrySendPii,
      // Per brand: jobalto's traffic alone outran the monthly span quota.
      tracesSampleRate: config.sentryTracesSampleRate ?? 0.1,
      // Links browser spans to backend spans so distributed traces show the
      // full client→server flow. Substring-matched against outgoing URLs.
      tracePropagationTargets: ['localhost', config.backendUrl],
      integrations: [Sentry.browserTracingIntegration()],
      denyUrls: [
        /extensions\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        // Cloudflare Zaraz tag manager — third-party, its 404s and parse
        // errors aren't actionable from our codebase.
        /\/cdn-cgi\/zaraz\//i,
        // Google AdSense throws TagError asynchronously inside its own
        // script when a slot's container is collapsed. Our AdSense wrapper
        // already guards the synchronous push; the throw originates in an
        // unreachable async context so we filter at the SDK boundary.
        /pagead\/js\/adsbygoogle/i,
      ],
      ignoreErrors: [
        'Failed to fetch',
        'NetworkError',
        'Network request failed',
        'Load failed',
        // 401 is expected when a session cookie expires; api/index.js dispatches
        // logout and re-throws so callers can short-circuit. It's not a bug.
        '401 unauthorized',
        // 404 is expected on deep links to deleted/never-existed resources
        // (stale Google index) and on optional sub-resources (vacancies,
        // sponsor, layout). Components handle it (404 page / empty section);
        // the rejection itself isn't a bug. Backstop for the component guards.
        '404 not found',
        // Sentry-generated message for promise rejections with no value/stack
        // — unactionable.
        'Non-Error promise rejection captured with value: undefined',
        // Belt-and-braces for the AdSense TagError in case denyUrls misses
        // (Safari sometimes reports the throw with a different frame URL).
        'No slot size for availableWidth',
        // web-vitals' CLS handler calls Array.prototype.at(), which pre-Chrome-92
        // engines lack (e.g. the HeadlessChrome 91 crawler that hits us). Bundled
        // third-party code on an ancient bot — not a real-user bug.
        'this.o.at is not a function',
      ],
      // Third-party scripts (AdSense creatives, tag managers) occasionally blow
      // the call stack inside their own async code. On iOS — smaller JSCore
      // stack limit — the throw surfaces via window.onerror with source
      // `undefined`, so the adsbygoogle denyUrl above can't match its frame
      // (PROFITROLL-FRONTEND-H). Drop stack overflows that carry no first-party
      // frame. We test for a *sourced* frame (a real https bundle URL) rather
      // than the in_app flag: Sentry marks the sourceless onerror frame in_app,
      // so an in_app check keeps exactly the noise we mean to drop. A genuine
      // recursion in our code always carries our https://…/assets/*.js frames
      // (beforeSend runs before sourcemap resolution, so filenames are still
      // runtime URLs) and is still reported. String-match ignoreErrors would
      // hide those too.
      beforeSend(event) {
        if (isCloudflareBeaconError(event)) return null;
        const values = event.exception?.values || [];
        const isStackOverflow = values.some(
          (v) => /Maximum call stack size exceeded/i.test(v.value || '')
        );
        if (!isStackOverflow) return event;
        const hasSourcedFrame = values.some((v) =>
          (v.stacktrace?.frames || []).some(
            (f) => f.filename && /^https?:\/\//i.test(f.filename)
          )
        );
        return hasSourcedFrame ? event : null;
      },
      beforeSendTransaction: dropResourceSpans,
    });
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initSentry, { timeout: 5000 });
  } else {
    setTimeout(initSentry, 2000);
  }
}
// ServiceMessage cannot be lazy: ErrorBoundary statically imports it as its
// crash fallback, which pulls it into the entry chunk anyway. Rolldown flags
// the parallel `lazy()` here as INEFFECTIVE_DYNAMIC_IMPORT.
const Auth = lazy(() => import('components/Auth'));

// Suppress only network/CORS unhandled rejections — those are expected when the
// API is unavailable. Everything else must bubble to the console and Sentry so
// real bugs stay visible.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage =
      event.reason?.message || event.reason?.toString() || String(event.reason) || '';
    const errorName = event.reason?.name || '';

    const isNetworkError =
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('CORS') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('blocked by CORS policy') ||
      (errorName === 'TypeError' && errorMessage.includes('fetch'));

    if (isNetworkError) {
      event.preventDefault();
      event.stopPropagation();
    }
  });
}

const rootElement = document.getElementById('root');

// Check if we're in react-snap pre-rendering mode
const IS_SNAP = navigator.userAgent === 'ReactSnap';

const App = () => {
  // Seed from i18n.isInitialized so the non-SNAP path (which only mounts
  // after i18nPromise resolves) renders content on the first commit —
  // otherwise useState(false) forced a render-null-then-render-content
  // sequence that caused a CLS spike after the prerendered HTML was wiped.
  const [i18nReady, setI18nReady] = useState(() => !!i18n.isInitialized);

  useEffect(() => {
    if (!i18nReady) i18nPromise.then(() => setI18nReady(true));
    // Pull backend feature flags once at boot so the menu and gated home
    // sections can drop entries whose feature is off for this environment
    // (e.g. consultants on jobalto). Failures are silently treated as
    // "everything off" by the reducer — safer than flashing UI on.
    store.dispatch(fetchFeatures()).catch(() => {});
  }, [i18nReady]);

  if (!i18nReady) {
    return null; // Or a loading spinner
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={createTheme(theme)}>
        <I18nextProvider i18n={i18n}>
          <Provider store={store}>
            {/* Auth mounts immediately so Firebase initializes and the API
                token is in storage before any auth-gated request fires.
                The wrapped components are lazy so they still code-split. */}
            <Suspense fallback={null}>
              <Auth>
                <ErrorBoundary>
                  <ServiceMessage>
                    <Routes />
                  </ServiceMessage>
                </ErrorBoundary>
              </Auth>
            </Suspense>
          </Provider>
        </I18nextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

// Mount once i18n resolves so we don't wipe the prerendered HTML before
// React can render content. createRoot intentionally replaces existing
// children — hydrateRoot was tried but consistently bailed to
// client-render due to lazy-component mismatches, so it offered no CLS
// benefit. The 0.24 home-page CLS that previously came from the Footer
// being removed during this swap is now fixed at the layout level by
// `minHeight: 100vh` on <main>, which keeps the Footer below the fold.
//
// In dev, Vite re-executes this entry module whenever an HMR update reaches it
// (any import without its own accept boundary, e.g. a plain data module).
// Re-running createRoot on the same container logs "createRoot() on a
// container that has already been passed to createRoot()" and mounts a second
// tree, so the root is kept on `window` across module re-executions and
// reused with root.render().
const mount = () => {
  if (window.__appRoot) {
    window.__appRoot.render(<App />);
    return;
  }
  if (rootElement.hasChildNodes()) rootElement.innerHTML = '';
  if (window.__rootHiddenForSkeleton) {
    rootElement.style.display = '';
    delete window.__rootHiddenForSkeleton;
  }
  window.__appRoot = ReactDOM.createRoot(rootElement);
  window.__appRoot.render(<App />);
};

if (IS_SNAP) {
  mount();
} else {
  i18nPromise.then(mount, mount);
}

// Self-accept HMR updates at the entry so Vite re-executes this module (and
// mount() re-renders into the existing root) instead of treating the entry
// as an unhandled boundary. plugin-react still falls back to a page reload
// afterwards because this file is not a pure component module; the root
// reuse above is what keeps that path free of duplicate-root errors.
if (import.meta.hot) {
  import.meta.hot.accept();
}
